/**
 * Servicio de Almacenamiento Local para Web (IndexedDB)
 * Gestiona el almacenamiento de datos locales del usuario registrado
 */

import { openDB } from 'idb';
import { getStorageType, StorageType } from '../utils/platformDetector';

const DB_NAME = 'shalom_local_db';
const DB_VERSION = 2; // ← INCREMENTADO para agregar índices userId

/**
 * Nombres de las tablas (object stores) en IndexedDB
 */
export const Tables = {
  EVENTOS: 'eventos',
  EVENT_CATEGORIES: 'event_categories',
  POSTS: 'posts',
  HISTORICAL_DATA: 'historical_data',
  BIBLE_NOTES: 'bible_notes',
  BIBLE_HIGHLIGHTS: 'bible_highlights',
  BIBLE_RELATIONS: 'bible_relations'
};

/**
 * Inicializa la base de datos IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
const initDB = async () => {
  // Verificar que estamos en un entorno con IndexedDB
  if (getStorageType() !== StorageType.INDEXEDDB) {
    throw new Error('IndexedDB no está disponible en esta plataforma');
  }

  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      console.log(`📦 Actualizando BD local de v${oldVersion} a v${newVersion}`);

      // ========================================
      // TABLA: EVENTOS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.EVENTOS)) {
        const eventStore = db.createObjectStore(Tables.EVENTOS, {
          keyPath: 'id',
          autoIncrement: true
        });
        eventStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        eventStore.createIndex('fecha', 'fechaInicio');
        eventStore.createIndex('tipo', 'tipo');
        eventStore.createIndex('categoria', 'categoria');
        eventStore.createIndex('estado', 'estado'); // 'borrador_local', 'enviado_revision', 'publicado'
        console.log('✅ Tabla "eventos" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const eventStore = transaction.objectStore(Tables.EVENTOS);
        if (!eventStore.indexNames.contains('userId')) {
          eventStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "eventos"');
        }
      }

      // ========================================
      // TABLA: CATEGORÍAS DE EVENTOS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.EVENT_CATEGORIES)) {
        const categoryStore = db.createObjectStore(Tables.EVENT_CATEGORIES, {
          keyPath: 'id',
          autoIncrement: true
        });
        categoryStore.createIndex('tipo', 'tipo'); // 'tipo', 'subtipo', 'categoria'
        categoryStore.createIndex('nombre', 'nombre');
        console.log('✅ Tabla "event_categories" creada');
      }

      // ========================================
      // TABLA: POSTS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.POSTS)) {
        const postStore = db.createObjectStore(Tables.POSTS, {
          keyPath: 'id',
          autoIncrement: true
        });
        postStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        postStore.createIndex('createdAt', 'createdAt');
        postStore.createIndex('estado', 'estado');
        postStore.createIndex('postIdRemoto', 'postIdRemoto');
        console.log('✅ Tabla "posts" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const postStore = transaction.objectStore(Tables.POSTS);
        if (!postStore.indexNames.contains('userId')) {
          postStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "posts"');
        }
      }

      // ========================================
      // TABLA: DATOS HISTÓRICOS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.HISTORICAL_DATA)) {
        const histStore = db.createObjectStore(Tables.HISTORICAL_DATA, {
          keyPath: 'id',
          autoIncrement: true
        });
        histStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        histStore.createIndex('createdAt', 'createdAt');
        histStore.createIndex('estado', 'estado');
        histStore.createIndex('datoIdRemoto', 'datoIdRemoto');
        console.log('✅ Tabla "historical_data" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const histStore = transaction.objectStore(Tables.HISTORICAL_DATA);
        if (!histStore.indexNames.contains('userId')) {
          histStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "historical_data"');
        }
      }

      // ========================================
      // TABLA: NOTAS BÍBLICAS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.BIBLE_NOTES)) {
        const noteStore = db.createObjectStore(Tables.BIBLE_NOTES, {
          keyPath: 'id',
          autoIncrement: true
        });
        noteStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        noteStore.createIndex('verseRef', 'verseRef'); // "Genesis 1:1"
        noteStore.createIndex('book', 'book');
        noteStore.createIndex('chapter', 'chapter');
        noteStore.createIndex('createdAt', 'createdAt');
        console.log('✅ Tabla "bible_notes" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const noteStore = transaction.objectStore(Tables.BIBLE_NOTES);
        if (!noteStore.indexNames.contains('userId')) {
          noteStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "bible_notes"');
        }
      }

      // ========================================
      // TABLA: SUBRAYADOS BÍBLICOS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.BIBLE_HIGHLIGHTS)) {
        const highlightStore = db.createObjectStore(Tables.BIBLE_HIGHLIGHTS, {
          keyPath: 'id',
          autoIncrement: true
        });
        highlightStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        highlightStore.createIndex('verseRef', 'verseRef');
        highlightStore.createIndex('book', 'book');
        highlightStore.createIndex('chapter', 'chapter');
        highlightStore.createIndex('color', 'color');
        highlightStore.createIndex('createdAt', 'createdAt');
        console.log('✅ Tabla "bible_highlights" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const highlightStore = transaction.objectStore(Tables.BIBLE_HIGHLIGHTS);
        if (!highlightStore.indexNames.contains('userId')) {
          highlightStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "bible_highlights"');
        }
      }

      // ========================================
      // TABLA: RELACIONES BÍBLICAS
      // ========================================
      if (!db.objectStoreNames.contains(Tables.BIBLE_RELATIONS)) {
        const relStore = db.createObjectStore(Tables.BIBLE_RELATIONS, {
          keyPath: 'id',
          autoIncrement: true
        });
        relStore.createIndex('userId', 'userId'); // ← CRÍTICO: Filtrar por usuario
        relStore.createIndex('verseFrom', 'verseFrom');
        relStore.createIndex('verseTo', 'verseTo');
        relStore.createIndex('relationType', 'relationType');
        relStore.createIndex('createdAt', 'createdAt');
        console.log('✅ Tabla "bible_relations" creada');
      } else if (oldVersion < 2) {
        // Migración v1 → v2: Agregar índice userId
        const relStore = transaction.objectStore(Tables.BIBLE_RELATIONS);
        if (!relStore.indexNames.contains('userId')) {
          relStore.createIndex('userId', 'userId');
          console.log('✅ Índice "userId" agregado a "bible_relations"');
        }
      }
    },
    
    blocked() {
      console.warn('⚠️ Base de datos bloqueada. Cierra otras pestañas de la aplicación.');
    },
    
    blocking() {
      console.warn('⚠️ Una versión más nueva de la BD está intentando abrirse.');
    },
    
    terminated() {
      console.error('❌ La conexión a la BD se cerró inesperadamente.');
    }
  });
};

/**
 * Servicio de almacenamiento local
 */
export const localStorageService = {
  /**
   * Inicializa la base de datos (útil para verificar)
   */
  async init() {
    try {
      await initDB();
      console.log('✅ Base de datos local inicializada correctamente');
      return true;
    } catch (error) {
      console.error('❌ Error al inicializar BD local:', error);
      throw error;
    }
  },

  // ==================== MÉTODOS CRUD GENÉRICOS ====================

  /**
   * Agrega un nuevo registro
   * @param {string} tableName - Nombre de la tabla
   * @param {Object} data - Datos a insertar
   * @returns {Promise<number>} ID del registro creado
   */
  async add(tableName, data) {
    try {
      const db = await initDB();
      const tx = db.transaction(tableName, 'readwrite');
      const store = tx.objectStore(tableName);
      
      const id = await store.add({
        ...data,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      await tx.done;
      console.log(`✅ Registro agregado en ${tableName}:`, id);
      return id;
    } catch (error) {
      console.error(`❌ Error al agregar en ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene un registro por ID
   * @param {string} tableName - Nombre de la tabla
   * @param {number} id - ID del registro
   * @returns {Promise<Object|undefined>}
   */
  async get(tableName, id) {
    try {
      const db = await initDB();
      return await db.get(tableName, id);
    } catch (error) {
      console.error(`❌ Error al obtener de ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene todos los registros de una tabla
   * @param {string} tableName - Nombre de la tabla
   * @returns {Promise<Array>}
   */
  async getAll(tableName) {
    try {
      const db = await initDB();
      return await db.getAll(tableName);
    } catch (error) {
      console.error(`❌ Error al obtener todos de ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene registros filtrados por índice
   * @param {string} tableName - Nombre de la tabla
   * @param {string} indexName - Nombre del índice
   * @param {any} value - Valor a buscar
   * @returns {Promise<Array>}
   */
  async getAllByIndex(tableName, indexName, value) {
    try {
      const db = await initDB();
      return await db.getAllFromIndex(tableName, indexName, value);
    } catch (error) {
      console.error(`❌ Error al obtener por índice de ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Actualiza un registro existente
   * @param {string} tableName - Nombre de la tabla
   * @param {number} id - ID del registro
   * @param {Object} data - Datos a actualizar
   * @returns {Promise<number>} ID del registro actualizado
   */
  async update(tableName, id, data) {
    try {
      const db = await initDB();
      const tx = db.transaction(tableName, 'readwrite');
      const store = tx.objectStore(tableName);
      
      await store.put({
        ...data,
        id,
        updatedAt: new Date().toISOString()
      });
      
      await tx.done;
      console.log(`✅ Registro actualizado en ${tableName}:`, id);
      return id;
    } catch (error) {
      console.error(`❌ Error al actualizar en ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Elimina un registro
   * @param {string} tableName - Nombre de la tabla
   * @param {number} id - ID del registro
   * @returns {Promise<void>}
   */
  async delete(tableName, id) {
    try {
      const db = await initDB();
      const tx = db.transaction(tableName, 'readwrite');
      await tx.objectStore(tableName).delete(id);
      await tx.done;
      console.log(`✅ Registro eliminado de ${tableName}:`, id);
    } catch (error) {
      console.error(`❌ Error al eliminar de ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Cuenta los registros de una tabla
   * @param {string} tableName - Nombre de la tabla
   * @returns {Promise<number>}
   */
  async count(tableName) {
    try {
      const db = await initDB();
      return await db.count(tableName);
    } catch (error) {
      console.error(`❌ Error al contar en ${tableName}:`, error);
      throw error;
    }
  },

  /**
   * Limpia todos los registros de una tabla
   * @param {string} tableName - Nombre de la tabla
   * @returns {Promise<void>}
   */
  async clear(tableName) {
    try {
      const db = await initDB();
      const tx = db.transaction(tableName, 'readwrite');
      await tx.objectStore(tableName).clear();
      await tx.done;
      console.log(`✅ Tabla ${tableName} limpiada`);
    } catch (error) {
      console.error(`❌ Error al limpiar ${tableName}:`, error);
      throw error;
    }
  },

  // ==================== MÉTODOS ESPECÍFICOS POR TABLA ====================

  // ---------- EVENTOS ----------
  eventos: {
    async agregar(evento) {
      return localStorageService.add(Tables.EVENTOS, {
        ...evento,
        estado: evento.estado || 'borrador_local'
      });
    },
    
    async obtenerTodos() {
      return localStorageService.getAll(Tables.EVENTOS);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.EVENTOS, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.EVENTOS, id);
    },
    
    async obtenerPorFecha(fecha) {
      const eventos = await localStorageService.getAll(Tables.EVENTOS);
      return eventos.filter(e => e.fechaInicio?.startsWith(fecha));
    },
    
    async actualizar(id, evento) {
      return localStorageService.update(Tables.EVENTOS, id, evento);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.EVENTOS, id);
    }
  },

  // ---------- CATEGORÍAS DE EVENTOS ----------
  eventCategories: {
    async agregar(categoria) {
      return localStorageService.add(Tables.EVENT_CATEGORIES, categoria);
    },
    
    async obtenerTodas() {
      return localStorageService.getAll(Tables.EVENT_CATEGORIES);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.EVENT_CATEGORIES, id);
    },
    
    async obtenerPorTipo(tipo) {
      return localStorageService.getAllByIndex(Tables.EVENT_CATEGORIES, 'tipo', tipo);
    },
    
    async actualizar(id, categoria) {
      return localStorageService.update(Tables.EVENT_CATEGORIES, id, categoria);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.EVENT_CATEGORIES, id);
    }
  },

  // ---------- POSTS ----------
  posts: {
    async agregar(post) {
      return localStorageService.add(Tables.POSTS, {
        ...post,
        estado: post.estado || 'borrador_local'
      });
    },
    
    async obtenerTodos() {
      return localStorageService.getAll(Tables.POSTS);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.POSTS, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.POSTS, id);
    },
    
    async obtenerPorEstado(estado) {
      return localStorageService.getAllByIndex(Tables.POSTS, 'estado', estado);
    },
    
    async actualizar(id, post) {
      return localStorageService.update(Tables.POSTS, id, post);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.POSTS, id);
    }
  },

  // ---------- DATOS HISTÓRICOS ----------
  historicalData: {
    async agregar(dato) {
      return localStorageService.add(Tables.HISTORICAL_DATA, {
        ...dato,
        estado: dato.estado || 'borrador_local'
      });
    },
    
    async obtenerTodos() {
      return localStorageService.getAll(Tables.HISTORICAL_DATA);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.HISTORICAL_DATA, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.HISTORICAL_DATA, id);
    },
    
    async obtenerPorEstado(estado) {
      return localStorageService.getAllByIndex(Tables.HISTORICAL_DATA, 'estado', estado);
    },
    
    async actualizar(id, dato) {
      return localStorageService.update(Tables.HISTORICAL_DATA, id, dato);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.HISTORICAL_DATA, id);
    }
  },

  // ---------- NOTAS BÍBLICAS ----------
  bibleNotes: {
    async agregar(nota) {
      return localStorageService.add(Tables.BIBLE_NOTES, nota);
    },
    
    async obtenerTodas() {
      return localStorageService.getAll(Tables.BIBLE_NOTES);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.BIBLE_NOTES, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.BIBLE_NOTES, id);
    },
    
    async obtenerPorVersiculo(verseRef) {
      return localStorageService.getAllByIndex(Tables.BIBLE_NOTES, 'verseRef', verseRef);
    },
    
    async obtenerPorLibro(book) {
      return localStorageService.getAllByIndex(Tables.BIBLE_NOTES, 'book', book);
    },
    
    async actualizar(id, nota) {
      return localStorageService.update(Tables.BIBLE_NOTES, id, nota);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.BIBLE_NOTES, id);
    },
    
    async buscar(query) {
      const notas = await localStorageService.getAll(Tables.BIBLE_NOTES);
      return notas.filter(nota => 
        nota.titulo?.toLowerCase().includes(query.toLowerCase()) ||
        nota.contenido?.toLowerCase().includes(query.toLowerCase())
      );
    }
  },

  // ---------- SUBRAYADOS BÍBLICOS ----------
  bibleHighlights: {
    async agregar(subrayado) {
      return localStorageService.add(Tables.BIBLE_HIGHLIGHTS, subrayado);
    },
    
    async obtenerTodos() {
      return localStorageService.getAll(Tables.BIBLE_HIGHLIGHTS);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.BIBLE_HIGHLIGHTS, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.BIBLE_HIGHLIGHTS, id);
    },
    
    async obtenerPorVersiculo(verseRef) {
      return localStorageService.getAllByIndex(Tables.BIBLE_HIGHLIGHTS, 'verseRef', verseRef);
    },
    
    async obtenerPorLibro(book) {
      return localStorageService.getAllByIndex(Tables.BIBLE_HIGHLIGHTS, 'book', book);
    },
    
    async obtenerPorColor(color) {
      return localStorageService.getAllByIndex(Tables.BIBLE_HIGHLIGHTS, 'color', color);
    },
    
    async actualizar(id, subrayado) {
      return localStorageService.update(Tables.BIBLE_HIGHLIGHTS, id, subrayado);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.BIBLE_HIGHLIGHTS, id);
    }
  },

  // ---------- RELACIONES BÍBLICAS ----------
  bibleRelations: {
    async agregar(relacion) {
      return localStorageService.add(Tables.BIBLE_RELATIONS, relacion);
    },
    
    async obtenerTodas() {
      return localStorageService.getAll(Tables.BIBLE_RELATIONS);
    },
    
    async obtenerPorUsuario(userId) {
      return localStorageService.getAllByIndex(Tables.BIBLE_RELATIONS, 'userId', userId);
    },
    
    async obtenerPorId(id) {
      return localStorageService.get(Tables.BIBLE_RELATIONS, id);
    },
    
    async obtenerPorVersiculo(verseRef) {
      const db = await initDB();
      const todas = await db.getAll(Tables.BIBLE_RELATIONS);
      return todas.filter(r => r.verseFrom === verseRef || r.verseTo === verseRef);
    },
    
    async obtenerPorTipo(relationType) {
      return localStorageService.getAllByIndex(Tables.BIBLE_RELATIONS, 'relationType', relationType);
    },
    
    async actualizar(id, relacion) {
      return localStorageService.update(Tables.BIBLE_RELATIONS, id, relacion);
    },
    
    async eliminar(id) {
      return localStorageService.delete(Tables.BIBLE_RELATIONS, id);
    }
  },

  // ---------- JERARQUÍA DE RELACIONES BÍBLICAS ----------
  bibleRelationHierarchy: {
    async agregar(hierarchy) {
      // Nota: Esta tabla no está en IndexedDB, se maneja en el backend
      // Por ahora, solo retornamos true para compatibilidad
      console.warn('⚠️ bibleRelationHierarchy.agregar: Esta funcionalidad requiere sincronización con el backend');
      return true;
    },
    
    async obtenerPorPadre(parentRelationId) {
      console.warn('⚠️ bibleRelationHierarchy.obtenerPorPadre: Esta funcionalidad requiere sincronización con el backend');
      return [];
    },
    
    async obtenerPorHijo(childRelationId) {
      console.warn('⚠️ bibleRelationHierarchy.obtenerPorHijo: Esta funcionalidad requiere sincronización con el backend');
      return [];
    }
  },

  // ==================== UTILIDADES ====================

  /**
   * Exporta toda la base de datos a JSON
   * @returns {Promise<Object>}
   */
  async exportAll() {
    const data = {};
    for (const tableName of Object.values(Tables)) {
      data[tableName] = await localStorageService.getAll(tableName);
    }
    return data;
  },

  /**
   * Obtiene estadísticas de la base de datos
   * @returns {Promise<Object>}
   */
  async getStats() {
    const stats = {};
    for (const tableName of Object.values(Tables)) {
      stats[tableName] = await localStorageService.count(tableName);
    }
    return stats;
  },

  /**
   * Limpia toda la base de datos (CUIDADO!)
   * @returns {Promise<void>}
   */
  async clearAll() {
    if (!confirm('⚠️ ¿Estás seguro de eliminar TODOS los datos locales? Esta acción no se puede deshacer.')) {
      return;
    }
    for (const tableName of Object.values(Tables)) {
      await localStorageService.clear(tableName);
    }
    console.log('✅ Base de datos local completamente limpiada');
  }
};

export default localStorageService;

